import { FieldState, FormState } from 'formstate';

import { FormValidators, InputFieldState } from '@servicetitan/form-state';

import {
    FileAttachmentCreateVm,
    FileAttachmentUpsertVm,
    IFileAttachmentVm,
    IMobileMaintenanceServiceEntryDetailsVm,
    MaintenanceServiceRecordUpsertVm,
    MobileMaintenanceServiceEntryCreateVm,
    MobileMaintenanceServiceEntryUpdateVm,
    ServiceEntryCategory,
    ServiceEntryState,
} from '@fleet-pro/mobile-webview-api';

import { SingleSelectOption } from '../../common/components/single-select';
import { getCategoryText } from './get-category-text';
import { getFileName } from './get-file-name';

export interface UploadFileDescriptor {
    file: File;
    uploadId: string;
}

export interface AttachedFileDescriptor {
    file: string;
    displayName: string;
    id: number;
}

export type ServiceEntryFileDescriptor = UploadFileDescriptor | AttachedFileDescriptor;

export type ServiceEntryFormType = ReturnType<typeof serviceEntryForm>;

interface ServiceRecordData {
    id?: number;
    serviceTypeId?: number;
    serviceType?: string;
    cost?: number;
}

const getDefaultServiceRecord = (): ServiceRecordData => ({
    id: Math.random(),
    serviceTypeId: undefined,
    serviceType: '',
    cost: 0,
});

const requiredOption = (message: string) => (value: SingleSelectOption | null) => {
    if (!value) {
        return message;
    }
};

export const serviceEntryForm = () => {
    const formState = new FormState({
        state: new InputFieldState<ServiceEntryState>(ServiceEntryState.Active),
        vin: new InputFieldState<string | undefined>(undefined).validators(FormValidators.required),
        category: new FieldState<SingleSelectOption | null>({
            id: ServiceEntryCategory.Scheduled,
            label: getCategoryText(ServiceEntryCategory.Scheduled),
        }).validators(requiredOption('Please select the category')),
        serviceDate: new InputFieldState<Date>(new Date()).validators(value => {
            if (!value) {
                return 'Please enter the service date';
            }
        }),
        odometerInMiles: new InputFieldState<number>(0),
        engineRunTimeInHours: new InputFieldState<number>(0),
        serviceCenter: new InputFieldState<string | undefined>(undefined),
        invoiceNumber: new InputFieldState<string | undefined>(undefined),
        notes: new InputFieldState<string | undefined>(undefined).validators((value: string) => {
            if (value && value.length > 256) {
                return 'Maximum of 256 characters allowed.';
            }
        }),
        serviceRecords: new FormState([serviceRecordFormState()]),
        fileAttachments: new FieldState<ServiceEntryFileDescriptor[]>([]),
    });

    formState.disableAutoValidation();

    return formState;
};

export const serviceRecordFormState = (serviceRecordData = getDefaultServiceRecord()) => {
    return new FormState({
        id: new InputFieldState<number | undefined>(serviceRecordData.id),
        serviceType: new FieldState<SingleSelectOption | null>(
            serviceRecordData.serviceType && serviceRecordData.serviceTypeId
                ? {
                      id: serviceRecordData.serviceTypeId.toString(),
                      label: serviceRecordData.serviceType,
                  }
                : null
        ).validators(requiredOption('Please select a service type')),
        cost: new InputFieldState<number>(serviceRecordData.cost ?? 0),
    });
};

export const formStateToCreateServiceEntryApiData = (
    form: ServiceEntryFormType
): MobileMaintenanceServiceEntryCreateVm => {
    const formValues = form.$;

    return MobileMaintenanceServiceEntryCreateVm.fromJS({
        state: formValues.state.value,
        vin: formValues.vin.value ?? '',
        serviceDate: formValues.serviceDate.value,
        category: formValues.category.value?.id as ServiceEntryCategory,
        odometerInMiles: formValues.odometerInMiles.value,
        engineRunTimeInHours: formValues.engineRunTimeInHours.value,
        serviceCenter: formValues.serviceCenter.value,
        invoiceNumber: formValues.invoiceNumber.value,
        notes: formValues.notes.value,
        fileAttachments: formValues.fileAttachments.value
            .filter(
                (item): item is UploadFileDescriptor => 'uploadId' in item && item.uploadId !== ''
            )
            .map(
                item =>
                    new FileAttachmentCreateVm({
                        name: getFileName(item),
                        uploadId: item.uploadId,
                    })
            ),
        serviceRecords: formValues.serviceRecords.$.map(
            record =>
                new MaintenanceServiceRecordUpsertVm({
                    id: record.$.id.value && record.$.id.value < 1 ? undefined : record.$.id.value,
                    serviceType: record.$.serviceType.value?.label ?? '',
                    serviceTypeId: record.$.serviceType.value?.id
                        ? Number(record.$.serviceType.value.id)
                        : undefined,
                    cost: record.$.cost.value,
                })
        ),
    });
};

export const formStateToUpdateServiceEntryApiData = (
    form: ServiceEntryFormType,
    serviceEntryId: number
): MobileMaintenanceServiceEntryUpdateVm => {
    const formValues = form.$;

    return MobileMaintenanceServiceEntryUpdateVm.fromJS({
        id: serviceEntryId,
        state: formValues.state.value,
        vin: formValues.vin.value ?? '',
        serviceDate: formValues.serviceDate.value,
        category: formValues.category.value?.id as ServiceEntryCategory,
        odometerInMiles: formValues.odometerInMiles.value,
        engineRunTimeInHours: formValues.engineRunTimeInHours.value,
        serviceCenter: formValues.serviceCenter.value,
        invoiceNumber: formValues.invoiceNumber.value,
        notes: formValues.notes.value,
        fileAttachments: formValues.fileAttachments.value.map(item => {
            if ('id' in item) {
                return new FileAttachmentUpsertVm({
                    id: item.id,
                    name: getFileName(item),
                });
            }

            return new FileAttachmentUpsertVm({
                name: getFileName(item),
                uploadId: (item as UploadFileDescriptor).uploadId,
            });
        }),
        serviceRecords: formValues.serviceRecords.$.map(
            record =>
                new MaintenanceServiceRecordUpsertVm({
                    id: record.$.id.value && record.$.id.value < 1 ? undefined : record.$.id.value,
                    serviceType: record.$.serviceType.value?.label ?? '',
                    serviceTypeId: record.$.serviceType.value?.id
                        ? Number(record.$.serviceType.value.id)
                        : undefined,
                    cost: record.$.cost.value,
                })
        ),
    });
};

export const apiDataToServiceEntryFormState = (
    serviceEntry: IMobileMaintenanceServiceEntryDetailsVm
): ServiceEntryFormType => {
    const form = serviceEntryForm();

    form.$.state.onChange(serviceEntry.state);
    form.$.vin.onChange(serviceEntry.vin);
    form.$.category.onChange({
        id: serviceEntry.category,
        label: getCategoryText(serviceEntry.category),
    });
    form.$.serviceDate.onChange(new Date(serviceEntry.serviceDate));
    form.$.odometerInMiles.onChange(serviceEntry.odometerInMiles ?? 0);
    form.$.engineRunTimeInHours.onChange(serviceEntry.engineRunTimeInHours ?? 0);
    form.$.serviceCenter.onChange(serviceEntry.serviceCenter);
    form.$.invoiceNumber.onChange(serviceEntry.invoiceNumber);
    form.$.notes.onChange(serviceEntry.notes);
    form.$.fileAttachments.onChange(
        serviceEntry.fileAttachments?.map((item: IFileAttachmentVm) => ({
            file: item.name,
            displayName: item.name,
            id: item.id,
        })) ?? []
    );

    const newServiceRecords = serviceEntry.serviceRecords.map(record =>
        serviceRecordFormState({
            id: record.id,
            serviceTypeId: record.serviceTypeId,
            serviceType: record.serviceType,
            cost: record.cost,
        })
    );

    form.$.serviceRecords.$.splice(0, form.$.serviceRecords.$.length, ...newServiceRecords);

    form.disableAutoValidation();

    return form;
};
